<template>
	<main>
		<section class="text-center rounded-b-lg" :class="$isMobile.any ? 'px-4 pt-10' : 'pt-20'">
			<h1 class="text-4xl text-text">Kostenlose Lebenslauf Vorlagen</h1>
			<h2 class="text-lg mb-10 text-text" :class="{ 'px-5': $isMobile.any }">
				Erstelle deinen CV in wenigen Minuten. Überzeuge bei deiner
				<br v-if="!$isMobile.any" />nächsten Bewerbung!
			</h2>
			<HokButton
				class="mx-auto"
				to="/hokifycv"
				color="main"
				invisible
				:class="{ 'mb-6': $isMobile.any }"
				>Jetzt Lebenslauf erstellen</HokButton
			>
			<div class="max-w-4xl mx-auto">
				<NuxtImg
					:src="
						$isMobile.any
							? '/img/b2c-lps/cv/LebenslaufDesignerMobile.png'
							: '/img/b2c-lps/cv/LebenslaufDesigner.png'
					"
					class="mx-auto w-full"
					:class="$isMobile.any ? 'aspect-4/5' : 'aspect-16/9'"
					alt="Lebenslauf Designer"
					ssr
				/>
			</div>
		</section>
		<section class="bg-grey-background" :class="$isMobile.any ? 'px-8 py-10' : 'py-20'">
			<div class="flex flex-col items-center max-w-6xl pb-20 mx-auto">
				<h2 class="text-text text-4xl font-bold">So funktioniert's!</h2>
				<h3 class="text-text text-center text-lg mb-12">
					Erstelle deinen kostenlosen Lebenslauf in drei Schritten online.
				</h3>
				<div class="mb-16" :class="{ 'flex max-w-6xl': !$isMobile.any }">
					<div
						v-for="(linkBox, index) in linkBoxes"
						:key="index"
						class="rounded-xl shadow-xl flex flex-col items-center text-center p-8"
						:class="$isMobile.any ? 'mb-5' : 'w-1/3 mx-6'"
					>
						<p
							class="border-2 border-main text-main font-bold rounded-full w-12 h-12 text-4xl leading-10 my-4"
						>
							{{ index + 1 }}
						</p>
						<p class="font-bold mb-1">{{ linkBox.headline }}</p>
						<p>{{ linkBox.text }}</p>
					</div>
				</div>
				<HokButton to="/hokifycv" invisible color="main">Lebenslauf erstellen</HokButton>
			</div>
		</section>
		<section class="bg-white" :class="$isMobile.any ? 'px-8 py-10' : 'py-20'">
			<div class="flex flex-col items-center max-w-6xl mx-auto">
				<ContentMediaSplitComponent
					v-for="(card, index) in cards"
					:key="index"
					class="mb-20"
					:content="card"
				/>
			</div>
		</section>
		<section class="bg-grey-background" :class="$isMobile.any ? 'px-8 py-10' : 'py-20'">
			<FAQ class="max-w-6xl mx-auto" :items="cvFAQs" title="Lebenslauf einfach erklärt" />
		</section>
		<section v-if="articles.length" :class="$isMobile.any ? 'px-4 py-10' : 'py-20'">
			<div :class="{ 'flex justify-between': !$isMobile.any }" class="max-w-6xl mx-auto pb-6">
				<div
					v-for="article in articles"
					:key="article._id"
					class="p-4"
					:class="{ 'w-1/3': !$isMobile.any }"
				>
					<ArticleCard :article="article" />
				</div>
			</div>
		</section>
		<section class="bg-grey-background py-20">
			<div
				class="max-w-2xl mx-auto"
				:class="$isMobile.any ? 'px-8' : 'flex flex-col items-center rounded-lg'"
			>
				<h2
					class="text-text text-2xl font-bold"
					:class="$isMobile.any ? 'text-start mb-6' : 'mb-12'"
				>
					So erstellst du deinen Lebenslauf
				</h2>
				<p>
					Erstelle deinen Lebenslauf ganz einfach kostenlos in wenigen Schritten direkt am
					Smartphone. Wähle aus modernen Design-Vorlagen und gib deinem Lebenslauf eine individuelle
					Farbe. Hole dir
					<HokLink
						to="/karriere/bewerbung/expertentipp-so-sticht-dein-lebenslauf-heraus"
						class="underline"
					>
						Expertentipps zum Thema Lebenslauf</HokLink
					>
					und Bewerbungsfoto. Schreibe deinen professionellen Lebenslauf jetzt online und lade ihn
					direkt als PDF-Dokument herunter!
				</p>
			</div>
		</section>
	</main>
</template>

<script lang="ts">
import type { IAPIArticle } from '@hokify/magazine-interface';
import FAQ from '@hokify/shared-components-nuxt3/lib/components/FAQ.vue';
import { mapStores } from 'pinia';
import { getAllAlternateUrls, getCanonicalUrl } from '~/helpers/metadata';
import ArticleCard from '~/components/website/ArticleCard.vue';
import cvFAQs from '~/data/b2c-lps/cv/cvFAQs.json';
import ContentMediaSplitComponent from '~/components/website/ContentMediaSplitComponent.vue';
import { useMagazineStore } from '@/stores/magazine';
import { useRootStore } from '@/stores/root';

export default defineNuxtComponent({
	name: 'Lebenslauf',
	components: {
		ContentMediaSplitComponent,
		FAQ,
		ArticleCard
	},
	setup() {
		definePageMeta({
			layout: 'default'
		});

		const rootStore = useRootStore();
		const path = `${rootStore.host}/lebenslauf-erstellen`;
		const canonical = getCanonicalUrl(path);
		const alternates = getAllAlternateUrls(path);
		const description =
			'Lebenslauf online erstellen: professionelle und moderne Lebenslauf Vorlagen für jeden Job.  Schnell, kostenlos und einfach!';
		const result: any = {
			title: 'Erstelle kostenlos deinen Lebenslauf aus Vorlagen!',
			link: [canonical, ...alternates],
			meta: [
				{ name: 'robots', content: 'index, follow' },
				{
					name: 'description',
					content: description
				}
			]
		};
		useHead(result);
	},
	computed: {
		...mapStores(useRootStore)
	},
	async asyncData({ $sentry, $pinia }) {
		const magazineStore = useMagazineStore($pinia);
		const articleAliases = [
			'lebenslauf-schreiben-vorlage',
			'bewerbung/lebenslauf-für-schüler-alle-fragen-und-antworten',
			'bewerbung/lücken-im-lebenslauf-was-tun'
		];
		const articles: IAPIArticle[] = [];
		try {
			for (const articleAlias of articleAliases) {
				const { article } = await magazineStore.loadArticle({
					alias: articleAlias
				});
				articles.push(article);
			}
		} catch (err: any) {
			$sentry.captureMessage(`article "${err?.response?.data?.message}" not found`);
		}

		return { articles };
	},
	data() {
		const articles = [] as IAPIArticle[];

		return {
			cvFAQs,
			articles,
			linkBoxes: [
				{
					headline: 'Einfach Anmelden',
					text: 'Gib deine E-Mail Adresse sowie deinen Vor- und Nachnamen ein und registriere dich bei hokify.'
				},
				{
					headline: 'Vervollständige deinen Lebenslauf',
					text: 'Leg direkt los und erstelle deinen Lebenslauf. Gib Persönliche Infos, Berufserfahrung und Ausbildungen an. '
				},
				{
					headline: 'Speichere deinen Lebenslauf',
					text: 'Klicke auf "Vorschau & Speichern", danach kannst du ein passendes Design und eine individuelle Farbe wählen.'
				}
			],
			cards: [
				{
					type: 'img',
					mediaPosition: 'right',
					src: 'img/b2c-lps/cv/LebenslaufDesignerSettings.png',
					title: 'hokify Lebenslauf-Designer',
					alt: 'hokify Lebenslauf-Designer Settings',
					text: 'Professioneller Lebenslauf gefällig? Erstelle deinen Lebenslauf aus modernen Design-Vorlagen. Ganz einfach, mit wenigen Schritten am Smartphone zum perfekten Lebenslauf!',
					buttonText: 'Lebenslauf erstellen',
					buttonLink: '/hokifycv',
					buttonInvisible: true
				},
				{
					type: 'img',
					mediaPosition: 'left',
					src: 'img/b2c-lps/cv/LebenslaufFarben.png',
					title: 'Wähle deine Lieblingsfarbe',
					alt: 'hokify Lebenslauf-Designer Farben',
					text: 'Gestalte deinen Lebenslauf in deiner Lieblingsfarbe und erstelle deinen modernen Lebenslauf mit individueller Farbgebung und Schrift. Hebe dich von anderen Bewerber:innen ab.\n',
					buttonText: 'Farbe wählen',
					buttonLink: '/hokifycv',
					buttonInvisible: true
				},
				{
					type: 'video',
					mediaPosition: 'right',
					poster: 'video/poster/application-photo-preview.jpeg',
					src: 'https://www.youtube.com/embed/ygqTIxpgMdE?mute=1&autoplay=1&cc_load_policy=1&cc_lang_pref=de',
					title: 'Selfie Bewerbungsfoto',
					alt: 'Bewerbungsfoto Tutorial',
					text: 'Erstelle dein Bewerbungsfoto ganz einfach im Selfie-Format am Smartphone. Wähle einen neutralen Hintergrund, achte auf das richtige Outfit. Wir haben die wichtigsten Infos zum Bewerbungsfoto in diesem Video für dich zusammengefasst.',
					buttonText: 'Foto aufnehmen',
					buttonLink: '/hokifycv',
					buttonInvisible: true
				}
			]
		};
	}
});
</script>
